import { ElementRef, forwardRef, Injector, Provider, SimpleChanges } from '@angular/core';
import { ControlContainer, FormControlName, NgControl, NG_ASYNC_VALIDATORS, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FORM_CONTROL_NAME_HOST } from './form-control-name-host-component';
import * as i0 from "@angular/core";
import * as i1 from "@angular/forms";
const controlNameBinding = {
  provide: NgControl,
  useExisting: forwardRef(() => FormControlName)
};
/**
 * for use with instructions system, formControlName directive must be "baked" into the input components using hostDirectives.
 * At the time of implementing, ng formControlName was not standalone and could thus not be used directly.
 * This custom directive solves it by extending formControl and providing a standalone version of it.
 * WARNING: using [formControlNameStandalone] in templates can cause an error stating detect double assignment of directive.
 * In most cases, just use regular formControlName instead.
 * Some components need to use formControlNameStandalone cause it provides additional data.
 * In this situation, an individual solution/workaround must be found.
 */
export let FormControlNameStandalone = /*#__PURE__*/(() => {
  class FormControlNameStandalone extends FormControlName {
    constructor(
    // Note: Standalone directive is constructed before host so e.g. ControlContainer will always be null!
    parent, validators, asyncValidators, valueAccessors,
    // NG_MODEL_WITH_FORM_CONTROL_WARNINGcannot be injected because provider is not exported from @angular/forms
    // @Optional() @Inject(NG_MODEL_WITH_FORM_CONTROL_WARNING) private _ngModelWarningConfig: string|null
    injector,
    // ElementRef works fine even though documentation says this directive is being constructed before the host element exists. Whatever.
    host, component) {
      super(parent, validators, asyncValidators, valueAccessors, null);
      this.injector = injector;
      this.host = host;
      this.component = component;
      this.initialized = false;
    }
    ngOnInit() {
      // FormControlNameStandalone is hardcoded as host directive on all input components. 
      // Means it will ALWAYS exist, even if the component is used with regular formControlName in parallel.
      // In that case, name will be null. In that case, it should not do anything.
      if (!this.name) return;
      if (this.component) {
        if (this.component.setFormControlName && typeof this.name === 'string') {
          this.component.setFormControlName(this.name);
        }
      }
      // we can inject ControlContainer now
      const controlContainer = this.injector.get(ControlContainer, null);
      if (!controlContainer) {
        const elementName = this.host.nativeElement?.tagName || 'UNKNOWN';
        throw new Error('formControlNameStandalone (<' + elementName + '>, name=' + this.name + ') failed to get ControlContainer');
      }
      // little hack to make parent logic work correctly
      this._parent = controlContainer;
      this.initialized = true;
    }
    ngOnChanges(changes) {
      // FormControlNameStandalone is hardcoded as host directive on all input components. 
      // Means it will ALWAYS exist, even if the component is used with regular formControlName in parallel.
      // In that case, name will be null. In that case, it should not do anything.
      if (!this.name) return;
      // only call super.ngOnChanges when Controlcontainer is available
      if (!this.initialized) this.ngOnInit();
      super.ngOnChanges(changes);
    }
    static {
      this.ɵfac = function FormControlNameStandalone_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FormControlNameStandalone)(i0.ɵɵdirectiveInject(i1.ControlContainer, 13), i0.ɵɵdirectiveInject(NG_VALIDATORS, 10), i0.ɵɵdirectiveInject(NG_ASYNC_VALIDATORS, 10), i0.ɵɵdirectiveInject(NG_VALUE_ACCESSOR, 10), i0.ɵɵdirectiveInject(i0.Injector), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(FORM_CONTROL_NAME_HOST, 10));
      };
    }
    static {
      this.ɵdir = /*@__PURE__*/i0.ɵɵdefineDirective({
        type: FormControlNameStandalone,
        selectors: [["", "formControlNameStandalone", ""]],
        features: [i0.ɵɵProvidersFeature([controlNameBinding]), i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature]
      });
    }
  }
  return FormControlNameStandalone;
})();
export function FormControlNameStandaloneHostDirectiveFactory() {
  return {
    directive: FormControlNameStandalone,
    inputs: ['formControlName: formControlNameStandalone'],
    outputs: []
  };
}